import { Base64FileReader } from '@Common/Controls/Base64FileReader';
import { IControl } from '@Common/Controls/Controls';
import { Button } from '@Common/Controls/SimpleButton';
import { px } from '@Common/Controls/Units';
import { safeDestroys } from '@Common/Core/Destroyable';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { MessageBox } from '@Common/Dialogs/MessageBox';

export interface IFileInputParameters {
    type?: string | string[]; // image by default
    name?: string; // ... by default
    width?: number | string;
    height?: number | string;
    loadByChunk?: boolean;
    container?: string;
}

function isSupportedFileReader(): boolean {
    // tslint:disable-next-line:no-string-literal
    return !!window['FileReader'];
}

export interface IFileInputResult {
    data: string;
    fileName: string;
}

export class FileInput implements IControl {
    private readonly parameters: IFileInputParameters;

    private readonly root: JQuery;
    private readonly input: JQuery;
    private readonly button: Button;

    private readonly eventBag = new MulticastDelegateBag();
    public readonly onFileSelected = this.eventBag.create<(fileName: string, size: number) => boolean | Promise<boolean>>({ once: true });

    public readonly onChunkLoaded = this.eventBag.create<(data: string, fileName: string, isFirstChunk: boolean) => JQueryPromise<any>>();
    public readonly onLoadedComplete = this.eventBag.create<(data: any) => void>();
    public readonly onChanged = this.eventBag.create<(data: string, fullName: string) => void>();
    public readonly onProgress = this.eventBag.create<(loaded: number, total: number) => void>();
    public readonly onCancel = this.eventBag.create<() => void>();

    private readonly chunkSize = 1024 * 1024; // 300Kb
    private reader: Base64FileReader;

    private readonly events = ['focus', 'mousemove', 'keydown'];
    private readonly eventHandler;

    constructor(params?: IFileInputParameters) {
        const defaultName = '...';
        this.parameters = $.extend(
            {
                type: 'image/*',
                name: defaultName,
                width: 30,
                height: 30,
                css: null,
                loadByChunk: false
            },
            params
        );

        this.eventHandler = () => {
            setTimeout(() => {
                const input = this.input[0] as HTMLInputElement;
                if (input.files.length == 0) {
                    this.onCancel();
                }
                this.setEvents(true);
            }, 500);
        };

        this.root = $('<div />').css({
            width: this.parameters.width,
            height: this.parameters.height,
            overflow: 'hidden'
        });

        this.button = new Button({
            width: '100%',
            height: '100%',
            minWidth: '0px',
            text: this.parameters.name
        });

        if (this.parameters.name === defaultName) {
            this.button.getControl().addClass('no-gap');
        }

        const accept = !_.isArray(this.parameters.type) ? [this.parameters.type] : this.parameters.type;

        this.input = $('<input />')
            .attr({ type: 'file', accept: _.join(accept, ',') })
            .css({
                width: '100%',
                height: '100%',
                opacity: 0,
                display: 'block',
                marginTop: '-' + px(this.parameters.height),
                cursor: 'pointer'
            })
            .on('click', evt => {
                this.setEvents();
            })
            .on('change', evt => {
                this.setEvents(true);
                const files = (evt.target as HTMLInputElement).files;
                if (files && files.length > 0) {
                    this.uploadFile(files[0]).shutdown();
                }
            })
            .hover(
                () => this.button.getControl().addClass('button-hover'),
                () => this.button.getControl().removeClass('button-hover')
            );

        this.root.append(this.button.getControl()).append(this.input);
    }

    private setEvents(remove = false): void {
        this.events.forEach(e => {
            if (remove) {
                document.body.removeEventListener(e, this.eventHandler);
            } else {
                document.body.addEventListener(e, this.eventHandler, { once: true });
            }
        });
    }

    public static openFileDialog(parameters: IFileInputParameters, changed: (data: string, fileName: string) => void): void;

    public static openFileDialog(parameters: IFileInputParameters): Promise<IFileInputResult>;
    public static openFileDialog(
        parameters: IFileInputParameters,
        changed?: (data: string, fileName: string) => void
    ): Promise<IFileInputResult> | void {
        return new Promise(resolve => {
            const root = $('body');
            parameters = $.extend(parameters, { width: '0xp', height: '0px' });
            const input = new FileInput(parameters);
            input.getControl().appendTo(root);
            input.onChanged((data: string, fileName: string) => {
                input.destroy();
                if (changed) {
                    changed(data, fileName);
                } else {
                    resolve({ data, fileName });
                }
            });
            input.click();
        });
    }

    public getControl(): JQuery {
        return this.root;
    }

    public enabled(value?: boolean): boolean {
        this.button.enabled(value);
        if (value) {
            this.input.removeAttr('disabled');
        } else {
            this.input.attr('disabled', 'disabled');
        }

        return this.button.enabled();
    }

    public visible(value?: boolean): boolean {
        if (value !== undefined) {
            this.root.toggle(value);
        }
        return this.root.is(':visible');
    }

    public readonly(readonly?: boolean): boolean {
        if (readonly !== undefined) {
            this.enabled(!readonly);
        }
        return !this.enabled();
    }

    public destroy(): void {
        this.setEvents(true);
        safeDestroys(this.root, this.button);
    }

    public setState(enabled: boolean): void {
        this.enabled(enabled);
    }

    public click(): void {
        if (isSupportedFileReader()) {
            document.body.focus();
            this.input.trigger('click');
        } else {
            const messageBox = new MessageBox({ text: strings.fileReaderSupport });
            messageBox.show();
        }
    }

    public abort(): void {
        if (this.reader) {
            this.reader.abort();
        }
    }

    public adjust(width: number, height: number): void {
        _.noop();
    }

    public async uploadFile(file: File): Promise<void> {
        const canProcessed = this.onFileSelected(file.name, file.size);
        const processed = await canProcessed;
        if (!processed && this.onFileSelected.canFire) {
            return;
        }

        this.reader = new Base64FileReader();
        if (this.parameters.loadByChunk) {
            for await (const [chunk, progress] of this.reader.readByChunk(file, this.chunkSize)) {
                const uploadResult = await this.onChunkLoaded(chunk, file.name, progress.index === 0);
                this.onProgress(progress.loaded, progress.total);
                if (progress.total === progress.loaded) {
                    this.onLoadedComplete(uploadResult);
                }
            }
        } else {
            this.onChanged(await this.reader.read(file), file.name);
        }
    }
}
